/**
 * D247 — a screen with unsaved edits asks before it is left, on both back paths.
 *
 * D188 (j) had *My account* autosave and carry no confirm button; D247 reverses that clause and
 * puts all three of its settings, and gate customization's three fields, behind an explicit Save.
 * The moment a screen holds a draft the platform does not, leaving it silently becomes a way to
 * lose work — so back stops being an exit and becomes a question.
 *
 * **There are two back paths and only one of them is a prop.** The app bar's arrow calls the
 * screen's own `onBack`; Android's hardware back reaches the app only as `popstate`, and the
 * router has no blocker API. So the guard is built out of D145's entry-owning primitive:
 * `useDismissOnBack` holds one history entry while the screen is dirty, and a hardware back spends
 * that entry instead of leaving the route.
 *
 * **`dirty && !prompting` is the whole design, and it is what makes the two paths converge.**
 * Both must end in the same history state — prompt open, guard entry *spent* — because the leave
 * that follows is `navigate.back()`, which is a raw `window.history.back()` (`Router.tsx`) and
 * unwinds nothing. A leave issued while the entry were still live would spend the entry instead of
 * the route and re-open the prompt in a loop.
 *
 *  - *hardware back*: the entry is popped, the router truncates its stack, and this hook's
 *    `openKeys` effect fires `onClose` → `prompting` goes true. The entry is already gone, and the
 *    push/close effect that follows is a no-op because `ownsEntry` was cleared by the pop.
 *  - *app-bar back*: `requestLeave()` sets `prompting` → `open` goes true→false while `ownsEntry`
 *    is still true, so `useDismissOnBack` calls `close()`, whose deferred, surplus-sized
 *    `history.go` spends the entry exactly as D145 requires.
 *  - *stay*: `prompting` goes false → `open` goes false→true, and the push effect re-arms. **The
 *    re-arm is why `prompting` is in the expression rather than a `dirty`-only guard.** With
 *    `open = dirty`, a hardware back clears `ownsEntry` while `open` never changes, the effect —
 *    keyed `[open, key]` — does not re-run, and no replacement entry is pushed: the *second*
 *    hardware back would then leave with no prompt at all. A generation counter in the key would
 *    also re-arm, but it re-pushes from inside the `popstate` handler, which is D145's
 *    skippable-`pushState` trap, and it leaves the two back paths in different history states.
 *
 * **The prompt itself deliberately owns no history entry.** Opening one would mean a `pushState`
 * issued from within a `popstate` handler — the case D145 records Chrome marking *skippable*. 🔸
 * The stated cost is that a hardware back *while the prompt is open* leaves the screen and
 * discards, which is the same outcome as the prompt's own discard action and the platform's own
 * convention for back, but it is not the prompt's third option. Screens that preview something
 * outside their own state (the appearance pill repaints the whole app) must therefore revert on
 * unmount as well as on discard — the guard cannot see every exit.
 *
 * **No unmount cleanup here, deliberately**, for `useDismissOnBack`'s own StrictMode reason: a
 * cleanup that traversed history would consume a real entry on a simulated unmount.
 */
import { useCallback, useRef, useState } from 'react'
import { useDismissOnBack } from './useDismissOnBack'

export interface LeaveGuard {
  /** Whether the "unsaved changes" prompt should be rendered. */
  readonly prompting: boolean
  /** The app bar's back arrow. Leaves immediately when clean; asks when dirty. */
  requestLeave(): void
  /** The prompt's "stay" answer. Closes it and re-arms the guard. */
  stay(): void
  /** Leave now — the prompt's discard answer, and a save that has committed. */
  leave(): void
}

export function useLeaveGuard(
  /** Stable and unique per screen, e.g. `preferences:leave`. */
  key: string,
  dirty: boolean,
  /** What "go back" means for this screen. Called only once the guard entry is spent. */
  onLeave: () => void,
): LeaveGuard {
  const [prompting, setPrompting] = useState(false)

  // Read through a ref for `useDismissOnBack`'s own reason: this is a fresh closure per render,
  // and depending on its identity would re-arm the guard on every keystroke in the screen.
  const onLeaveRef = useRef(onLeave)
  onLeaveRef.current = onLeave

  useDismissOnBack(
    key,
    dirty && !prompting,
    useCallback(() => setPrompting(true), []),
  )

  const requestLeave = useCallback(() => {
    if (!dirty) {
      onLeaveRef.current()
      return
    }
    setPrompting(true)
  }, [dirty])

  const stay = useCallback(() => setPrompting(false), [])

  /**
   * Deliberately does **not** clear `prompting`. Doing so would re-arm a guard entry in the same
   * commit as the navigation, leaving a spent entry behind for the next back tap to absorb — the
   * dead-tap failure D145 exists to remove. The screen is unmounting; the flag goes with it.
   */
  const leave = useCallback(() => onLeaveRef.current(), [])

  return { prompting, requestLeave, stay, leave }
}
